// IndexedDB access. The count's input is the append-only event log (engine/log.ts, stores `events` and `meta`).
// The `taps` store (S1/S2) now holds truth-mode taps only, in the format the truth export reads, and the
// taps it held before the engine existed (engine/log.ts converts those once). `calibration` is one row per output device.
// Storage can be refused (private windows, cleared site data); callers must keep working in memory when it is.

export interface TapEvent {
  readonly id: string;
  readonly track: string;
  /** Corrected file time of the tap (heard minus the output calibration offset), seconds. The count uses this. */
  readonly time: number;
  /** Heard file time before calibration, seconds. Absent on taps saved before calibration existed. */
  readonly raw?: number;
  readonly kind: 'one';
  /** Wall-clock ms when the tap was made. */
  readonly wall: number;
  /** Set by "Undo tap" in truth mode: the tap is kept in the store but is not a truth tap (Put back clears it). */
  readonly removed?: true;
}

/** The lag of one output device, measured by the click track. */
export interface Calibration {
  readonly device: string;
  /** Seconds the listener hears after the clock says: taps are corrected by subtracting this. */
  readonly offsetS: number;
  /** Number of taps that went into the median. */
  readonly taps: number;
  readonly madeAt: string;
}

const DB_NAME = 'salsacount';
const DB_VERSION = 3;
const TAPS = 'taps';
const CALIBRATION = 'calibration';
/** The event log (engine/log.ts): append-only, one log per track, ordered by the auto-increment key `n`. */
export const EVENTS = 'events';
/** Small flags (engine/log.ts): key to value. */
export const META = 'meta';

export function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      // Each store is created only when missing, so any older version upgrades in place.
      const db = req.result;
      if (!db.objectStoreNames.contains(TAPS)) {
        db.createObjectStore(TAPS, { keyPath: 'id' }).createIndex('track', 'track');
      }
      if (!db.objectStoreNames.contains(CALIBRATION)) {
        db.createObjectStore(CALIBRATION, { keyPath: 'device' });
      }
      if (!db.objectStoreNames.contains(EVENTS)) {
        db.createObjectStore(EVENTS, { keyPath: 'n', autoIncrement: true }).createIndex('track', 'track');
      }
      if (!db.objectStoreNames.contains(META)) {
        db.createObjectStore(META, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Resolves when the transaction has committed (not when a request in it succeeded: a quota failure comes at commit), rejects if it aborts. */
export function committed(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('storage refused the write'));
    tx.onabort = () => reject(tx.error ?? new Error('storage refused the write'));
  });
}

export async function addTap(ev: TapEvent): Promise<void> {
  const db = await open();
  try {
    const tx = db.transaction(TAPS, 'readwrite');
    const done = committed(tx);
    tx.objectStore(TAPS).add(ev);
    await done;
  } finally {
    db.close();
  }
}

/** Mark a truth tap removed (Undo tap) or bring it back (Put back). The row stays, so nothing is lost. */
export async function setTapRemoved(ev: TapEvent, removed: boolean): Promise<void> {
  const db = await open();
  try {
    const tx = db.transaction(TAPS, 'readwrite');
    const done = committed(tx);
    const { removed: _was, ...rest } = ev;
    tx.objectStore(TAPS).put(removed ? { ...rest, removed: true } : rest);
    await done;
  } finally {
    db.close();
  }
}

export async function tapsFor(track: string): Promise<TapEvent[]> {
  const db = await open();
  try {
    const events = await request<TapEvent[]>(
      db.transaction(TAPS, 'readonly').objectStore(TAPS).index('track').getAll(track),
    );
    return events.sort((a, b) => a.time - b.time);
  } finally {
    db.close();
  }
}

export async function saveCalibration(c: Calibration): Promise<void> {
  const db = await open();
  try {
    const tx = db.transaction(CALIBRATION, 'readwrite');
    const done = committed(tx);
    tx.objectStore(CALIBRATION).put(c);
    await done;
  } finally {
    db.close();
  }
}

export async function loadCalibration(device: string): Promise<Calibration | undefined> {
  const db = await open();
  try {
    return await request<Calibration | undefined>(
      db.transaction(CALIBRATION, 'readonly').objectStore(CALIBRATION).get(device),
    );
  } finally {
    db.close();
  }
}
